import { useState } from 'react';
import { type LucideIcon, AlertTriangle, WifiOff, RefreshCw, ChevronLeft, Home } from 'lucide-react';
import { useAppTranslations } from '@/hooks/useAppTranslations';

type Domain = 'golf' | 'people' | 'admin';

export type ErrorStateVariant = 'network' | 'crash' | 'chunk' | 'generic';

interface ErrorStateProps {
  /** Override the icon — defaults to AlertTriangle (or WifiOff for network variant) */
  icon?: LucideIcon;
  title?: string;
  description?: string;
  domain?: Domain;
  variant?: ErrorStateVariant;
  /** Show retry button. Pass handler — component manages loading state internally. */
  onRetry?: () => void | Promise<void>;
  onGoBack?: () => void;
  onGoHome?: () => void;
  /** Dev-only error detail shown under a disclosure (also accepts raw Error or string via `error` alias) */
  devError?: Error | null;
  /** Alias for devError — accepts Error | string for backward compatibility */
  error?: Error | string | null;
  /** When true, show error detail disclosure (dev-only guard still applies) */
  showDetails?: boolean;
  className?: string;
}

const domainStyles: Record<Domain, { ring: string; icon: string; btn: string; halo: string }> = {
  golf: {
    ring: 'bg-gradient-to-br from-emerald-100 to-green-200',
    icon: 'text-[#1B4332]',
    btn: 'bg-[linear-gradient(135deg,#52B788_0%,#2D5A3D_50%,#1B4332_100%)]',
    halo: '0 0 0 12px rgba(82,183,136,0.08)',
  },
  people: {
    ring: 'bg-gradient-to-br from-[#2A4494] to-[#1E3270]',
    icon: 'text-white',
    btn: 'bg-[linear-gradient(135deg,#2A4494_0%,#1E3270_100%)]',
    halo: '0 0 0 12px rgba(42,68,148,0.12)',
  },
  admin: {
    ring: 'bg-gradient-to-br from-slate-100 to-slate-200',
    icon: 'text-slate-700',
    btn: 'bg-[linear-gradient(135deg,#475569_0%,#1e293b_100%)]',
    halo: '0 0 0 12px rgba(71,85,105,0.08)',
  },
};


export function ErrorState({
  icon,
  title,
  description,
  domain = 'golf',
  variant = 'generic',
  onRetry,
  onGoBack,
  onGoHome,
  devError,
  error,
  showDetails = false,
  className = '',
}: ErrorStateProps) {
  const resolvedDevError: Error | null = devError ??
    (error instanceof Error ? error : error ? new Error(String(error)) : null);
  const [retrying, setRetrying] = useState(false);
  const { t } = useAppTranslations();

  const styles = domainStyles[domain];
  const es = t.ui.errorState;

  const variantCopy: Record<ErrorStateVariant, { title: string; description: string }> = {
    network: es.network,
    chunk: es.chunk,
    crash: es.crash,
    generic: es.generic,
  };
  const copy = variantCopy[variant];

  const DisplayIcon = icon ?? (variant === 'network' ? WifiOff : AlertTriangle);
  const displayTitle = title ?? copy.title;
  const displayDescription = description ?? copy.description;

  const handleRetry = () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    setTimeout(() => onRetry(), 600);
  };

  return (
    <div
      className={`bg-white border border-slate-200 rounded-[20px] px-7 py-10 text-center shadow-sm ${className}`}
      style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}
    >
      {/* Icon ring */}
      <div
        className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${styles.ring}`}
        style={{ boxShadow: styles.halo }}
      >
        <DisplayIcon size={44} strokeWidth={1.75} className={styles.icon} />
      </div>

      {/* Copy */}
      <h3 className="text-[20px] font-extrabold text-slate-900 leading-tight tracking-tight mb-2.5">
        {displayTitle}
      </h3>
      <p className="text-sm text-slate-500 leading-relaxed mb-7 max-w-[260px] mx-auto">
        {displayDescription}
      </p>

      {/* Actions */}
      <div className="flex flex-col gap-2.5">
        {onRetry && (
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className={`${styles.btn} text-white font-bold text-[15px] tracking-tight py-3.5 px-6 rounded-[14px] w-full border-none cursor-pointer flex items-center justify-center gap-2 transition-opacity disabled:opacity-70`}
          >
            <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? es.actions.retrying : es.actions.tryAgain}
          </button>
        )}
        {onGoBack && (
          <button
            type="button"
            onClick={onGoBack}
            className="bg-transparent border border-slate-200 text-slate-600 font-semibold text-sm py-3 px-6 rounded-[14px] w-full cursor-pointer flex items-center justify-center gap-2 hover:border-slate-300 hover:text-slate-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {es.actions.goBack}
          </button>
        )}
        {onGoHome && (
          <button
            type="button"
            onClick={onGoHome}
            className="bg-transparent text-slate-400 font-semibold text-sm py-2 px-6 rounded-[14px] w-full cursor-pointer flex items-center justify-center gap-2 hover:text-slate-600 transition-colors"
          >
            <Home className="w-4 h-4" />
            {es.actions.goHome}
          </button>
        )}
      </div>

      {/* Dev-only error detail */}
      {(import.meta.env.DEV || showDetails) && resolvedDevError && (
        <details className="mt-6 text-left">
          <summary className="cursor-pointer text-xs text-slate-400 select-none">Dev: error details</summary>
          <pre className="mt-2 text-xs text-red-600 bg-red-50 p-3 rounded-xl overflow-auto">
            {resolvedDevError.message}
          </pre>
        </details>
      )}
    </div>
  );
}
